import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import { useOutsideClick } from '../../../common/useOutsideClick'
import { Button } from '../../../components/Button/Button'

export interface UserOption {
  id: string
  label: string
}

interface UserFilterProps {
  users: UserOption[]
  selectedUserIds: string[]
  disabled?: boolean
  isLoading?: boolean
  onSelectionChange: (ids: string[]) => void
}

export const UserFilter: FC<UserFilterProps> = ({ users, selectedUserIds, disabled, isLoading, onSelectionChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingSelection, setPendingSelection] = useState<string[]>(selectedUserIds)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasSelectedItems = pendingSelection.length > 0

  const closeDropdown = useCallback(
    (selection: string[]) => {
      setIsOpen(false)
      setSearchTerm('')
      const prev = new Set(selectedUserIds)
      if (selection.length !== prev.size || selection.some((id) => !prev.has(id))) {
        onSelectionChange(selection)
      }
    },
    [selectedUserIds, onSelectionChange],
  )

  useOutsideClick(containerRef, () => {
    if (!isOpen) {
      return
    }
    closeDropdown(pendingSelection)
  })

  const [debouncedSearchTerm] = useDebounce(searchTerm, 300)

  const filteredUsers = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase()
    if (!term) {
      return users
    }
    return users.filter((u) => u.label.toLowerCase().includes(term))
  }, [users, debouncedSearchTerm])

  const handleToggle = useCallback((userId: string, checked: boolean) => {
    setPendingSelection((prev) => {
      if (!checked) {
        return prev.filter((id) => id !== userId)
      }
      if (prev.includes(userId)) {
        return prev
      }
      return [...prev, userId]
    })
  }, [])

  const handleFilterButtonClick = () => {
    if (isOpen) {
      closeDropdown(pendingSelection)
    } else {
      setPendingSelection(selectedUserIds)
      setIsOpen(true)
    }
  }

  const handleClearSelectionClick = () => {
    setPendingSelection([])
  }

  const displayLabel = useMemo(() => {
    if (selectedUserIds.length === 0) {
      return 'All users'
    }
    if (selectedUserIds.length === 1) {
      const match = users.find((u) => u.id === selectedUserIds[0])
      return match?.label ?? '1 user'
    }
    return `${selectedUserIds.length} users`
  }, [selectedUserIds, users])

  return (
    <div className='insights-user-filter' ref={containerRef}>
      <button
        type='button'
        className='insights-user-filter__trigger'
        aria-haspopup='true'
        aria-expanded={isOpen}
        aria-controls='user-filter-dropdown'
        onClick={handleFilterButtonClick}
        disabled={disabled}
      >
        <i className='bi bi-people' />
        <span className='insights-user-filter__label'>{displayLabel}</span>
        <i className={classNames('bi', isOpen ? 'bi-chevron-up' : 'bi-chevron-down')} />
      </button>
      {isOpen && (
        <div id='user-filter-dropdown' className='insights-user-filter__dropdown'>
          <div className='insights-user-filter__search'>
            <i className='bi bi-search' />
            <Form.Control
              type='text'
              size='sm'
              placeholder='Search users'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              autoFocus
            />
          </div>
          <div className='insights-user-filter__list'>
            {isLoading && filteredUsers.length === 0 ? (
              <div className='text-muted small p-2'>Loading users…</div>
            ) : (
              <>
                {filteredUsers.map((user) => (
                  <Form.Check
                    key={user.id}
                    type='checkbox'
                    id={`user-filter-${user.id}`}
                    label={user.label}
                    checked={pendingSelection.includes(user.id)}
                    onChange={(e) => handleToggle(user.id, e.target.checked)}
                  />
                ))}
                {filteredUsers.length === 0 && <div className='text-muted small p-2'>No users found</div>}
              </>
            )}
          </div>
          {hasSelectedItems && (
            <Button variant='link' className='insights-user-filter__clear' onClick={handleClearSelectionClick}>
              Clear selected users
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
